import { ReactNode } from 'react';
import { NavLink } from 'react-router';
import styled, { css } from 'styled-components';

import { ThemeConfig } from '#/service/config/config.types';
import { ButtonVariant, MenuFocusStyle } from '#/service/constants';
import { remify } from '#/service/utils';

import {
  getBorderRadius,
  getFontWeight,
  getVariantStyle,
} from '../Button/Button';
import { focusShadow } from '../styles';

export const transProps = '300ms ease-in-out';

export interface LinkStyleProps {
  theme: ThemeConfig;
  $focusStyle?: MenuFocusStyle;
  $buttonVariant?: ButtonVariant;
  $inHamburger?: boolean;
}

// only dim the icon and text for link style and ghost button variant
const dimTextAndIconOnHover = (buttonVariant: ButtonVariant | undefined) =>
  (!buttonVariant || buttonVariant === ButtonVariant.Ghost) &&
  css`
    &:hover span.title,
    &:hover svg {
      opacity: 0.7;
    }
  `;

const lineHighlightStyle = `
  content: '';
  position: absolute;
  bottom: ${remify(-16)};
  left: 0;
  right: 0;
  height: ${remify(4)};
  background-color: var(--menuHighlightBackground);
  min-width: ${remify(24)};
`;

export const linkStyle = ({
  theme,
  $focusStyle = MenuFocusStyle.Box,
  $buttonVariant,
  $inHamburger,
}: LinkStyleProps) => css`
  display: flex;
  position: relative;
  align-items: center;
  text-decoration: none;
  box-sizing: border-box;
  cursor: pointer;
  font: var(--fontStyleSubtitle);

  ${$inHamburger
    ? css`
        white-space: normal;
        height: auto;
        min-height: ${remify(40)};
        width: 100%;

        & span.title {
          text-align: center;
          overflow-wrap: anywhere;
        }

        & span.box {
          justify-content: center;
          margin: 0;
          box-sizing: border-box;
        }
      `
    : css`
        white-space: nowrap;
        height: ${remify(80)};
        border-bottom: ${remify(4)} solid transparent;
        border-top: ${remify(4)} solid transparent;

        & span.title {
          text-overflow: ellipsis;
          max-width: 100%;
        }
      `}

  & .menuItem {
    position: relative;
  }

  ${dimTextAndIconOnHover($buttonVariant)}

  ${!$buttonVariant &&
  // no button variant -> link style (but override global style), menu colors, active bg, etc
  css`
    &&& {
      color: var(--menuForeground);
      text-decoration: none;
      opacity: 1;
    }

    ${$inHamburger
      ? `
        padding: ${remify(8)} 0;
      `
      : `
        padding-left: ${remify(24)};
      `}

    &.active {
      ${$focusStyle === MenuFocusStyle.Line &&
      ` 
          .menuItem::after { 
            ${lineHighlightStyle}
          }
        `};

      ${$focusStyle === MenuFocusStyle.Box &&
      ` > span.box {
          color: var(--menuHighlightForeground);
          background: var(--menuHighlightBackground);
        }
      `};
    }
  `}

  ${$buttonVariant &&
  // button stlye, variant colors, no active bg, etc
  css`
    border: none;
    outline: none;
    cursor: pointer;
    ${!$inHamburger &&
    `
      margin: ${remify(18)} 0;
      height: auto;
    `}
    background: transparent;
    font: inherit;
    line-height: normal;

    &:hover {
      opacity: 1;
    }

    && {
      padding: ${remify(16)};
      ${getVariantStyle({ theme, $variant: $buttonVariant })}
      ${getBorderRadius({ theme, $variant: $buttonVariant })}
      ${getFontWeight({ theme, $variant: $buttonVariant })}
      ${$buttonVariant === ButtonVariant.Ghost &&
      `color: var(--menuForeground);`}
    }

    &.active {
      ${$focusStyle === MenuFocusStyle.Line &&
      `
        margin: 0;
        border-radius: 0;
        .menuItem::after {
            ${lineHighlightStyle}
          }
      `}

      ${$focusStyle === MenuFocusStyle.Box &&
      ` 
        color: var(--menuHighlightForeground);
        background: var(--menuHighlightBackground);
      `};
    }
  `}

  &:focus-visible {
    ${focusShadow}
  }
`;

export const MenuGradient = styled.div`
  position: absolute;
  inset: 0;
  padding: 0;
  background: linear-gradient(0deg, transparent, var(--menuGradientBackground));
  z-index: -1;
  pointer-events: none;
  opacity: 1;
  transition: opacity ${transProps};
`;

interface TransparentModeProps {
  $isTransparent?: boolean;
}

export const transparentModeStyles = css<TransparentModeProps>`
  /*
   * Force the background transparent and hide the gradient to override what the
   * $isCollapsed rule sets on always-collapsed transparent routes (like /registration).
   * Only emit styles in transparent mode. These declarations are last in the
   * cascade, so emitting them otherwise would break the $isCollapsed rule.
   * Leaving them out lets the base styles / $isCollapsed control the gradient.
   */
  ${({ $isTransparent }) =>
    $isTransparent &&
    css`
      background: transparent;

      & > ${MenuGradient} {
        opacity: 0;
      }
    `}
`;

export const MenuNavLink = styled(NavLink).attrs({
  'data-testid': 'menu-nav-link',
})`
  ${linkStyle};
`;

MenuNavLink.displayName = 'MenuNavLink';

export const MenuLink = styled.a`
  ${linkStyle};
`;

MenuLink.displayName = 'MenuLink';

export const MenuButton = styled.button`
  ${linkStyle};

  border-radius: ${remify(5)};
`;

MenuButton.displayName = 'MenuButton';

interface MenuItemBoxBgProps {
  $iconOnly?: boolean;
}

export const MenuItemBoxBg = styled.span<MenuItemBoxBgProps>`
  display: flex;
  align-items: center;
  line-height: normal;
  padding: ${remify(9)} ${remify(16)};
  margin: 0 ${remify(-16)};
  border-radius: ${remify(5)};
  min-width: 100%;

  ${({ $iconOnly }) =>
    $iconOnly &&
    `
    padding: ${remify(10)};
    border-radius: 100rem;
  `}
`;

MenuItemBoxBg.displayName = 'MenuItemBoxBg';

interface MenuNavProps {
  $isCollapsed?: boolean;
  $isTransparent?: boolean;
  $isHidden?: boolean;
  children?: ReactNode;
  theme: ThemeConfig;
}

export const MenuNav = styled.nav<MenuNavProps>`
  font: var(--fontStyleSubtitle);
  display: flex;
  z-index: 1;
  align-items: center;
  color: var(--pageParagraphForeground);
  line-height: ${remify(80)};
  user-select: none;
  background: transparent;
  transition:
    background ${transProps},
    transform ${transProps};

  & > * {
    padding: 0 ${remify(20)};
  }

  & .icon {
    height: 1.3rem;
    width: 1.3rem;
  }
  ${({ $isCollapsed }) =>
    $isCollapsed &&
    `
      padding-bottom: 0;
      background: var(--menuOpaqueBackground);
      pointer-events: auto;
      & > ${MenuGradient} {
        opacity: 0;
      }
    `};

  /* 105% to make sure that even the focus outline is off screen */
  ${({ $isHidden }) => $isHidden && 'transform: translateY(-105%);'}

  ${transparentModeStyles}
`;
